import React from 'react'
import BigSpinner from 'common/components/BigSpinner'
import { DataTable } from 'common/components/DataTable'
import { ColumnDefinition } from 'common/components/DataTable'
import PaginationBox from 'common/components/PaginationBox'
import { usePaginatedFetch } from 'common/hooks/usePaginatedFetch'
import Title from "common/components/Title"

interface Props {
    title?: string
    description?: React.ReactNode
    columns: ColumnDefinition[]
    endpoint: string
    endpointParams?: Record<string, unknown>
    beforeTable?: React.ReactNode
    afterTable?: React.ReactNode
    dataKey?: unknown
    selectable?: boolean
    onSelectionChange?: (selectedIds: number[]) => void
    paginationExtra?: React.ReactNode
    summaryRows?: Array<Array<React.ReactNode | import('common/components/DataTable').CellDefinition>>
}

function GenericList({ title, description, columns, endpoint, endpointParams, beforeTable, afterTable, dataKey, selectable, onSelectionChange, paginationExtra, summaryRows }: Props) {
    const { results, error, hasMore, start, sort, fetchValues } = usePaginatedFetch(endpoint, endpointParams, dataKey)
    const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())

    React.useEffect(() => {
        setSelectedIds(new Set())
    }, [results])

    React.useEffect(() => {
        if (onSelectionChange) onSelectionChange(Array.from(selectedIds))
    }, [selectedIds]) // eslint-disable-line react-hooks/exhaustive-deps

    const allIds: number[] = selectable && results ? (results as any[]).map(r => r.id).filter((id: unknown) => typeof id === 'number') : []
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id))

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(allIds))
        }
    }

    const toggleRow = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const effectiveColumns = selectable
        ? [
            new ColumnDefinition(
                '',
                (obj: any, _i: number) => (
                    <input
                        type="checkbox"
                        checked={selectedIds.has(obj.id)}
                        onChange={() => toggleRow(obj.id)}
                        onClick={e => e.stopPropagation()}
                    />
                ),
                'td-checkbox',
                'th-checkbox'
            ) as unknown as ColumnDefinition,
            ...columns
          ]
        : columns

    const checkboxHeader = selectable
        ? <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Select all on this page" />
        : undefined

    const needsPagination = start > 0 || hasMore
    const paginationBox = needsPagination
        ? <PaginationBox fetchCallback={fetchValues} start={start} limit={50} sort={sort} hasMore={hasMore} />
        : null

    const paginationRow = (needsPagination || paginationExtra)
        ? <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {paginationBox}
            {paginationExtra && selectedIds.size > 0 && <div>{paginationExtra}</div>}
          </div>
        : null

    return (
        <div>
            {title && <Title title={title} />}
            {description && <p>{description}</p>}
            {beforeTable}
            {paginationRow}
            {error && <p>Error!</p>}
            {results === null ? <BigSpinner /> : <DataTable columns={effectiveColumns as unknown as ColumnDefinition[]} rows={results as unknown as Record<string, unknown>[]} checkboxHeader={checkboxHeader} onRowClick={selectable ? (_, obj) => toggleRow((obj as any).id) : undefined} summaryRows={summaryRows} sort={sort} onSortChange={(newSort) => fetchValues(0, newSort)} />}
            {paginationRow}
            {afterTable}
        </div>
    )
}

export default GenericList
